import { RefObject } from 'react'
import { IGroupVotingButton, IVotingButton } from './voting'
import { ArtistProps } from 'components/modules/Calendar/types'

export type Nullable<T> = T | null

export interface FetchedStories {
  pages: Story[]
}

export interface PageContent {
  blocks: Array<ComponentItem>
  seo: SeoProps
  slug: string
  title: string
}

export interface Story {
  name: string
  content: {
    body: PageContent
  }
}

export interface PageData {
  preview: boolean
  slug: string
  story: Story
  lostandfoundData?: string | null
}

export interface ResolvedPagesData {
  pages: PageData[]
}

export interface Twitter {
  handle?: string
  site?: string
  cardType?: string
}

export interface VideoProps {
  id?: string
  autoPlay?: boolean
  backgroundVideoXl?: AssetVideoType
  backgroundVideoMd?: AssetVideoType
  backgroundVideoSm?: AssetVideoType
  className?: string
  posterImageXl?: AssetType
  posterImageMd?: AssetType
  posterImageSm?: AssetType
  videoLoop: boolean
  externalVideoRef?: RefObject<HTMLVideoElement>
  handlePlaybackFailed?: (status: boolean) => void
  preload?: 'auto' | 'none'
  isPaused?: boolean
  vimeoIdXl?: string
  vimeoIdSm?: string
  vimeoId?: string
}

export type RichTextType = { html: string } | null

export interface SeoProps {
  allowSearchEnginesToShowThisPage: boolean
  canonical?: string
  defaultTitle: string
  facebook?: { appId: string }
  ogImage: {
    url: string
  }
  ogTitle: string
  ogType: string
  ogDescription: string
  metaDescription: string
  seoTitle: string
  shouldSearchEnginesFollowLinks: boolean
  twitter?: Twitter
  canonicalUrl: string
}

export interface HeroGeneralInfoProps {
  id?: string
  headline: string
  copy: RichTextType
  infoItems: Array<HeroInfoBarLinkProp>
  backgroundImageXl: ImageProps
  backgroundImageSm: ImageProps
  showBackgroundImage: boolean
}

export interface NavigationHeaderProps {
  id?: string
  navigationMenuItems: NavigationMenuItemsProps
  brandLogo: {
    url: string
    fileName: string
  }
  buttonTitle: string
  buttonIcon?: 'calendar' | 'mail' | 'work'
  buttonUrl?: string
  isTargetBlank: boolean
  bannerRef?: any
  endNavItem?: string
  endNavItemUrl?: string
}

export interface NotificationBannerProps {
  id?: string
  copy: RichTextType
  ctaPrimary?: ButtonProps
  showBanner: boolean
}

export interface ClassificationProps {
  id?: string
  title: string
  description?: string
  ratingNumber?: number
  ratingImage?: AssetType
}

// @TODO: determine the TRUE alt of the three
export interface ImageProps {
  alt?: string
  fileName?: string
  id?: string
  image: {
    url: string
    altText?: string
  }
  width?: number
  height?: number
  componentName?: string | 'Image'
}

export type ImageComponentProps = Pick<ImageProps, 'alt' | 'image' | 'componentName'>
export type AssetType = Pick<ImageProps, 'fileName' | 'width' | 'height'> & {
  altText?: string
  url: string
}
export type AssetVideoType = Pick<AssetType, 'fileName' | 'url'>
export interface HeroInfoBarLinkProp {
  image: {
    image: ImageProps
  }
  anchorText: string
  url: string
}

export interface InfoBarSectionProps {
  subheadline: string
  description: RichTextType
  cta: ButtonProps
  icon: { url: string; altText?: string }
  iconHeight: number
  iconWidth: number
  index: number
  id?: string
  highlightedWords: string
  gradientColor: string
  displayGradient: boolean
  gradientPlacementOnTop: boolean
  subheadlineColor?: string
  eyebrow?: string
  img?: ImageProps
  mobileImage?: ImageProps
  defaultImg?: ImageProps
  image?: ImageProps
}

export const EducationLevels: string[] = [
  '',
  '',
  '',
  '',
  '4th Grade',
  '5th Grade',
  '6th Grade',
  '7th Grade',
  '8th Grade',
  '9th Grade',
  '10th Grade',
  '11th Grade',
  '12th Grade',
  'Freshman',
  'Sophomore',
  'Junior',
  'Senior',
  'Grad Student'
]

export interface URLProps {
  //anchor: ? @TODO: determine what `anchor` prop type is
  cached_url?: string
  fieldtype: 'multilink' | string // @TODO: determine other possible values
  id?: string
  linktype: 'story' | 'url'
  url: string
}

export type SubNavLinksType = {
  id: string
  isTargetBlank: boolean
  title: string
  url: string
  hasDivider: boolean
  hasAccentDivider?: boolean
  groupTitle?: string
  sectionLinks?: Array<{
    title: string
    url: string
  }>
}

export interface NavigationMenuItemProps {
  id: string
  componentName: 'NavigationMenuItem'
  isTargetBlank: boolean
  subNavLinks?: Array<SubNavLinksType>
  title: string
  url: string
  submenuImage: {
    url: string
  }
  submenuImageTitle: string
  submenuImageSubtitle: string
  submenuRedirectUrl: string
  hasDivider: boolean
  hasAccentDivider?: boolean
}

export interface WidgetSectionProps {
  id: string
  ctaBtnText: string
  ctaBtnFormId: string
  disclaimer: string
  subtitle: string
  title: string
}

export interface NavigationColumnProps {
  columnStyleClass: string
  navigationMenuItems: Array<NavigationMenuItemProps>
  title: string
}

export interface BottomNavProps {
  bottomNav: Array<NavigationMenuItemProps>
  legalText: string
  sponsorLogo: any
  taologo: any
}

export interface NavigationColumnsProps extends Array<NavigationColumnProps> {}

export interface NavigationMenuItemsProps extends Array<NavigationMenuItemProps> {}

export interface ButtonGroupProps {
  id?: string
  btnGroupTitle: string
  buttons: Array<WidgetSectionProps>
  bottomDivider?: boolean
}

export interface LocationProps {
  id?: string
  ctaText: string
  isLastChild?: boolean
  title: string
  url: string
}

export interface VimeoProps {
  id?: string
  url: string
  videoTitle?: string
  videoDescription?: string
  thumbnailUrl?: string
  uploadDate?: string
  isChildOfSmallCard?: boolean
  isChildOfCard?: boolean
  posterImage?: {
    url: string
    fileName: string
  }
  externalUrl: string
  componentName: string | 'Vimeo'
}

export interface CarouselSlideProps {
  componentName?: string | 'CarouselSlide'
  defaultImg: ImageProps
  description: string
  handleImageClick: Function
  id?: string
  imageDescription: string
  imageTitle: string
  img: ImageProps
  mobileImage: ImageProps
  openImageInModal?: boolean
  title: string
}

export type ArtistIntroCardType = {
  image?: AssetType
  logoImage?: AssetType
  title: string
}

export interface DoesNotExistProps {
  backgroundImage?: AssetType
  buttonGroup: Array<ButtonProps>
  id?: string
}

export interface GtmTypes {
  businessUnit?: string | 'entertainment'
  clickPreviousPv?: string | 'shows' | 'other_events'
  clickType?: string | 'cta' | 'navigation'
  clickSource?: string
  clickTracking?: string
  event?: string | 'click_buy' | 'click_content' | 'click_nav' | 'click_thumbnail' | 'pageview'
  eventData?: {
    eventCategory?: string | null
    eventDate?: string | null
    eventType?: string | null
    ticketmasterEventId?: string
    venueName?: 'sphere'
  }
  linkLocation?: string
  linkModule?: string
  linkPosition?: string | null
  linkType?: string | 'internal' | 'external' | 'interactive' | 'fevo'
  navType?: string | 'header' | 'footer'
  pageType?: string
  skipEvent?: boolean
}

export type ClickEvt = React.MouseEvent<HTMLAnchorElement | HTMLButtonElement>

export interface ButtonProps {
  analytics?: GtmTypes
  anchorLink?: string
  color?: string | 'primary' | 'dark' | 'light' | 'gold'
  componentName?: string | 'Button'
  disabled?: boolean
  fill?: string | 'hollow' | 'solid' | 'text'
  id?: string
  isFullWidth?: boolean
  isTargetBlank?: boolean
  justify?: string | 'left' | 'center' | 'right'
  modal?: ModalProps | undefined
  onClick?: ((e: ClickEvt) => void) | null
  size?: string | 'xs' | 'sm' | 'md' | 'lg'
  title: string
  url?: string | null
}

export interface HeadingProps {
  children?: React.ReactNode | string
  color?: string | 'accent' | 'dark' | 'light' | 'lightest' | 'primary' | 'primary-light'
  componentName?: string | 'ContentHeading'
  id?: string
  level: string | number | 1 | 2 | 3 | 4 | 5 | 6
  levelDisplay?: string | 1 | 2 | 3 | 4 | 5 | 6 | 'display' | 'eyebrow'
  justify?: 'left' | 'center'
  noMarginTop?: boolean
  noMarginBottom?: boolean
  showHeading?: boolean
  title?: string
}

export interface EventProps {
  category: Array<string>
  date_time: number
  event_type: Array<string>
  formatted_start_time: string
  host_url: string
  id: string
  is_sold_out: boolean
  start_time: string
  name: string
  price: PriceProps
  spanMultipleDays?: boolean
  end_date?: string
  timezone?: string
}

interface ArtistProps {
  id: string
  artistImage: ImageProps
  artistName: string
}

interface PriceProps {
  min: number
  max: number
}

export interface EventDateProps {
  category: string
  date_time: string
  end_date: string
  spanMultipleDays: boolean
  artists: Array<ArtistProps>
  date: number
  event_type: Array<string>
  events: Array<EventProps>
  formatted_start_time: string
  hidePricing: boolean
  host_url: string
  id: string
  is_best_value: boolean
  is_holiday: boolean
  is_on_sale: boolean
  is_sold_out: boolean
  is_sponsored_day: boolean
  name: string
  price: PriceProps
  sponsors: Array<String>
  venue_id: string
}

export interface EventDatesAPIResponseProps {
  results: Array<EventDateProps>
}

export interface NavProps {
  noOfMonths: number
  startDate: string
}

export interface MonthProps {
  label: string
  value: string
  shortLabel: string
}

export interface CalendarNavProps {
  calendarData: Map<string, object> | object
  calendarFilterItems: Array<MonthProps>
  selectedMonth: MonthProps
  allEvents: Array<EventDateProps>
}

export interface CalendarEdpProps {
  id?: string
  calendarId: string
  componentName: string | 'Calendar'
  ctaText: string
  heading: string
  overrideApiSoldOutStatus: boolean
  soldOutConfirmationModalText: string
  title?: string
  hidePricing: boolean
  seo?: any
  artist: ArtistProps
}

export interface EventDatesProps {
  eventDates: Array<EventDateProps>
}

export interface PillProps {
  id?: string
  eyebrow?: string
  headline?: string
  description?: string
  ctaText: string
  ctaURL: string
}

export interface ParagraphProps {
  id?: string
  color?: string | 'light' | 'lightest'
  componentName?: string | 'Paragraph'
  fontSize?: string | 'lg' | 'md' | 'sm' | 'xs'
  justify?: 'center' | 'left'
  noMarginBottom?: boolean
  text: RichTextType
  readMoreText?: RichTextType
}

export interface ShowsCardProps {
  id?: string
  backgroundImage: {
    url: string
    redirectUrl?: string
  }
  buttonGroup?: Array<ButtonProps>
  headline?: string
  eyebrow?: string
  subheadline?: string
  description?: RichTextType
}

export interface FormProps {
  componentName?: string | 'Form'
  formId: string
  handleModalClose?: () => void
  id?: string
  inputBgColor?: 'black' | 'transparent' | 'white'
  isChildOfModal?: boolean
  submitButtonBgColor?: 'white'
  submitButtonText?: string
  showSuccessMessageInModal?: boolean
  formSuccessLogo?: AssetType
  modalSuccessMessage?: RichTextType
  modalSuccessTitle?: string
}

export interface ContentModuleSectionProps {
  id?: string
  blocks: Array<ParagraphProps | VimeoProps | ButtonProps | HeadingProps | ImageProps | FormProps | ModalProps>
  showDividerAtBottom: boolean
  showDividerAtTop: boolean
  dividerFullWidth?: boolean
  contentModuleDivId?: string
}

export interface ContentModuleProps {
  moduleSections: Array<ContentModuleSectionProps>
}

export interface SocialMediaItemProps {
  icon: ImageProps
  platformName: string
  url: string
}

export interface PartnershipItemProps {
  icon: ImageProps
  platformName: string
  url: string
  newtab: boolean
}

export interface SocialMediaRowProps {
  align: string | 'center' | 'left'
  socialMediaRowHeadline?: string
  socialMediaRow: Array<SocialMediaItemProps>
}

export interface PartnershipRowProps {
  align: string | 'center' | 'left'
  partnershipRowHeadline?: string
  partnershipRow: Array<PartnershipItemProps>
}

export interface FooterProps {
  id?: string
  generalInformation: {
    title: string
    addressLine1: string
    addressLine2: string
    phoneNumber: string
  }
  legalText: string
  logoPrimary: {
    url: string
  }
  logoSecondary: {
    url: string
  }
  navMenu1: NavigationMenuItemsProps
  navMenu2: NavigationMenuItemsProps
  navMenu3: NavigationMenuItemsProps
  navMenuLegal: NavigationMenuItemsProps
  socialMediaRow: Array<SocialMediaItemProps>
  partnershipRow: Array<PartnershipItemProps>
  socialMediaRowHeadline: string
}

export interface LeaderboardProps {
  id?: string
  educationLevel: number
  artWorkTitle: string
  schoolName: string
  firstName: string
  lastName: string
  posterImage: string
  voteCount: number
  _id: string
}

export interface GalleryProps {
  galleryPlaylist?: Array<any>
  tabLabels?: Array<string>
  resultsHeading: string
  activeTab?: string
  id: string
  filterHeading: string
  filterValue: string
  sortByHeading: string
  sortByOptions: string[]
  isLeaderBoard: Boolean
  heroTitle: string
  heroDescription: RichTextType
  heroBgImage?: ImageProps
  votingModalReminder?: string
  votingModalDisclaimer?: string
  votingDeadline?: string
  groupVotingButton: IGroupVotingButton
  isWinnersView?: boolean
}

export interface CohortVotingButtonProps {
  id?: string
  title: string
  votingButtons: IVotingButton[]
}

export interface IVideo {
  url: string
  posterImage: string
  title: any
  uploadDate: string
}

export interface IconButtonsProps {
  id: string
  enable: boolean
  icon: AssetType
  cta: string
  url: string
  openLinkInNewTab: boolean
}

export interface IconButtonsCardProps {
  id?: string
  enable: boolean
  eyebrow?: string
  headline?: string
  backgroundImage: AssetType
  backgroundVideo?: {
    url: string
  }
  iconButtons: Array<IconButtonsProps>
}

export interface BackgroundVideoProps {
  video: { url: string }[]
  posterImage: {
    url: string
    fileName: string
  }[]
}

export interface BackgroundMedia extends ImageProps {
  id: string
  componentName: string
}

export interface GroupTicketingCardProps {
  id: string
  media: BackgroundMedia
  headline?: string
  eyebrow?: string
  eyebrowColor?: string
  description?: RichTextType
  backgroundGradient: string
  backgroundColor?: {
    hex: string
  }
  buttonGroup?: Array<ButtonProps>
  hasMediaInRightColumn?: boolean
  groupTicketingCardId: string
  heading?: string
  headlineIsCenterAligned?: boolean
  eyebrowIsCenterAligned?: boolean
  descriptionIsCenterAligned?: boolean
  buttonGroupIsCenterAligned?: boolean
}

export interface ISignUpProps {
  componentName: string
  id: string
  heading: string
  headingIsCenterAligned?: boolean
  body: RichTextType
  bodyIsCenterAligned?: boolean
  disclaimer: RichTextType
  disclaimerIsCenterAligned?: boolean
  submitButton: ButtonProps
  redirectAfterFormSubmit?: string
  buttonGroup?: Array<ButtonProps>
  buttonsAreCenterAligned?: boolean
  confirmationModalText: RichTextType
  backgroundMedia: BackgroundMedia
  mobileBackgroundMedia: BackgroundMedia
  formId: string
  checkboxIsDisabled?: boolean
}

export interface ISignUpFormElements extends HTMLFormControlsCollection {
  name: HTMLInputElement
  email: HTMLInputElement
  age: HTMLInputElement
}

export interface ISignUpSubmission extends HTMLFormElement {
  elements: ISignUpFormElements
}

export interface ctaSectionTitle {
  title: string
}

export interface ModalProps {
  backgroundImageXl?: AssetType | null
  backgroundImageSm?: AssetType | null
  blocks?: Array<ModalDynamicComponentType> | undefined
  children?: any
  closeOnOutsideClick?: boolean
  componentName?: string | 'Modal'
  form?: FormProps | undefined
  handleModalClose: () => void
  id?: string
  isOpen?: boolean
  type: string | 'event_announcement' | 'form' | 'calendar_event' | 'form_success' | 'media'
}

export type ModalDynamicComponentType = ButtonProps | HeadingProps | ParagraphProps | FormProps | EyebrowProps

export interface ModalDynamicComponentProps {
  block: ModalDynamicComponentType | undefined
}

export interface DynamicComponentProps {
  block: ComponentItem
  isChildOfModal?: boolean
}

export interface EyebrowProps {
  id?: string
  backgroundColor?: string | 'transparent' | 'orange' | 'primary' | 'secondary' | 'purple'
  componentName?: string | 'Eyebrow'
  isBold?: boolean
  size?: string | 'sm' | 'md' | 'lg' | 'xl'
  color?: string | 'accent' | 'dark' | 'gray' | 'light' | 'lighter' | 'primary'
  justify?: string | 'center' | 'left'
  noMarginBottom?: boolean
  noMarginTop?: boolean
  title: string
}

export interface LostandFoundProps {
  id?: string
  componentName: string | 'LostandFound'
  description: RichTextType
  headline: string
  lostandfoundData: string | null
}

export interface ResponsiveMultimedia {
  backgroundVideoXl?: AssetVideoType
  backgroundVideoMd?: AssetVideoType
  backgroundVideoSm?: AssetVideoType
  posterImageXl?: AssetType
  posterImageMd?: AssetType
  posterImageSm?: AssetType
  videoLoop?: boolean
  vimeoIdXl?: string
  vimeoIdSm?: string
}
export interface ResponsiveImage {
  imageMd?: AssetType
  imageSm?: AssetType
  imageXl: AssetType
}

export type GradientType = 'radial_gradient' | 'linear_gradient' | 'frosted_glass' | 'transparent'
export type FontSizeType = 'md-plus' | 'md'
export type CardBackgroundColorType =
  | 'tealDark'
  | 'pinkLight'
  | 'greenLight'
  | 'dark'
  | 'gold'
  | 'purple'
  | 'transparent'

export type EyeBrowColorType = 'light' | 'accent' | 'primary' | 'dark'

export type HeadlineType = Pick<HeadingProps, 'levelDisplay' | 'level' | 'justify' | 'title'>

export type CardType = {
  id?: string
  backgroundGradient?: GradientType
  backgroundColor?: CardBackgroundColorType
  overrideBackgroundColor?: CardBackgroundColorType
  centerCopyOnMobile?: boolean
  ctas?: ButtonProps[]
  description?: RichTextType
  hasDescriptionListCheckMark?: boolean
  hasReadMoreSection?: boolean
  hasOverrideStyles?: boolean
  hasVideo?: boolean
  headline?: string
  image?: AssetType
  mediaPosition?: 'left' | 'right'
  readMore?: RichTextType
  stackMediaAndCopy?: boolean
  subheadline?: string
  subheadlineColor?: EyeBrowColorType
  overrideSubheadlineColor?: EyeBrowColorType
  cardsDescriptionFontSize?: FontSizeType
  video?: AssetVideoType
}

// @TODO: continue to build upon this type
export type ComponentItem =
  | ButtonProps
  | CalendarEdpProps
  | EyebrowProps
  | FormProps
  | HeadingProps
  | ImageProps
  | LostandFoundProps
  | ModalProps
  | ParagraphProps
  | VimeoProps
